import { Injectable } from '@angular/core';
import { Plani8DataService } from '@monorepo/services';
import {
  TREQUEST_TNA_TARGET_UPDATES_CONFIG_UPDATE,
  TREQUEST_UPDATE_BENCHMARKING_CONFIG,
  TREQUEST_UPDATE_EFFICIENCY_CONFIG,
  TREQUEST_UPDATE_EXPECTED_DATE_CONFIG,
  TREQUEST_UPDATE_OTL_OPL_CONFIG,
  TREQUEST_UPDATE_SED_CONFIG,
  TREQUEST_UPDATE_AUTO_PLAN_CONFIG,
  TRESPONSE_AUTO_PLAN_CONFIG,
} from '@monorepo/types';
import { select, Store } from '@ngrx/store';
import { catchError, lastValueFrom, of } from 'rxjs';
import { TPlani8State } from '../reducers';
import * as FactoryConfigActions from './factory-config.actions';
import * as FactoryListSelectors from './factory-config.selectors';

@Injectable({
  providedIn: 'root',
})
export class FactoryConfigFacade {
  constructor(private store: Store<TPlani8State>, private plani8DataService: Plani8DataService) {}

  // A one-off read outside the store, for the Auto Plan modal's OPL cap. null when the request fails; callers fall back
  // to the backend's own checks.
  loadAutoPlanConfig(): Promise<TRESPONSE_AUTO_PLAN_CONFIG | null> {
    return lastValueFrom(this.plani8DataService.fetchAutoPlanConfig().pipe(catchError(() => of(null))));
  }

  fetchFactoryEfficiencyConfig() {
    this.store.dispatch(new FactoryConfigActions.GetFactoryEfficiencyConfig());
  }

  setEfficiencyLoader(showLoader: boolean) {
    this.store.dispatch(new FactoryConfigActions.SetEfficiencyLoader(showLoader));
  }

  fetchFactoryBenchmarkingConfig() {
    this.store.dispatch(new FactoryConfigActions.GetFactoryBenchmarkingConfig());
  }

  setBenchmarkingLoader(showLoader: boolean) {
    this.store.dispatch(new FactoryConfigActions.SetBenchmarkingLoader(showLoader));
  }

  fetchFactoryOtlOplConfig() {
    this.store.dispatch(new FactoryConfigActions.GetFactoryOtlOplConfig());
  }

  setOtlOplLoader(showLoader: boolean) {
    this.store.dispatch(new FactoryConfigActions.SetOtlOplLoader(showLoader));
  }

  fetchFactorySedConfig() {
    this.store.dispatch(new FactoryConfigActions.GetFactorySedConfig());
  }

  setSedLoader(showLoader: boolean) {
    this.store.dispatch(new FactoryConfigActions.SetSedLoader(showLoader));
  }

  fetchFactoryExpectedDateConfig() {
    this.store.dispatch(new FactoryConfigActions.GetFactoryExpectedDateConfig());
  }

  setExpectedDateLoader(showLoader: boolean) {
    this.store.dispatch(new FactoryConfigActions.SetExpectedDateLoader(showLoader));
  }

  fetchTnaTargetUpdatesConfig() {
    this.store.dispatch(new FactoryConfigActions.GetTnaTargetUpdatesConfig());
  }

  setTnaTargetUpdatesConfigLoader(showLoader: boolean) {
    this.store.dispatch(new FactoryConfigActions.SetTnaTargetUpdatesConfigLoader(showLoader));
  }

  fetchAllFactoryConfigs() {
    this.store.dispatch(new FactoryConfigActions.GetAllFactoryConfigs());
  }

  getFactoryEfficiencyConfig() {
    return this.store.pipe(select(FactoryListSelectors.getFactoryEfficiencyConfig));
  }

  getEfficiencyLoader() {
    return this.store.pipe(select(FactoryListSelectors.getEfficiencyLoader));
  }

  getFactoryBenchmarkingConfig() {
    return this.store.pipe(select(FactoryListSelectors.getFactoryBenchmarkingConfig));
  }

  getBenchmarkingLoader() {
    return this.store.pipe(select(FactoryListSelectors.getBenchmarkingLoader));
  }

  getFactoryOtlOplConfig() {
    return this.store.pipe(select(FactoryListSelectors.getFactoryOtlOplConfig));
  }

  getOtlOplLoader() {
    return this.store.pipe(select(FactoryListSelectors.getOtlOplLoader));
  }

  getFactorySedConfig() {
    return this.store.pipe(select(FactoryListSelectors.getFactorySedConfig));
  }

  getSedLoader() {
    return this.store.pipe(select(FactoryListSelectors.getSedLoader));
  }

  getFactoryExpectedDateConfig() {
    return this.store.pipe(select(FactoryListSelectors.getFactoryExpectedDateConfig));
  }

  getTnaTargetUpdatesConfig() {
    return this.store.pipe(select(FactoryListSelectors.getTnaTargetUpdatesConfig));
  }

  getExpectedDateLoader() {
    return this.store.pipe(select(FactoryListSelectors.getExpectedDateLoader));
  }

  updateFactoryEfficiencyConfig(payload: TREQUEST_UPDATE_EFFICIENCY_CONFIG) {
    this.store.dispatch(new FactoryConfigActions.UpdateFactoryEfficiencyConfig(payload));
  }

  updateFactoryBenchmarkingConfig(payload: TREQUEST_UPDATE_BENCHMARKING_CONFIG) {
    this.store.dispatch(new FactoryConfigActions.UpdateFactoryBenchmarkingConfig(payload));
  }

  updateFactoryOtlOplConfig(payload: TREQUEST_UPDATE_OTL_OPL_CONFIG) {
    this.store.dispatch(new FactoryConfigActions.UpdateFactoryOtlOplConfig(payload));
  }

  updateFactorySedConfig(payload: TREQUEST_UPDATE_SED_CONFIG) {
    this.store.dispatch(new FactoryConfigActions.UpdateFactorySedConfig(payload));
  }

  updateFactoryExpectedDateConfig(payload: TREQUEST_UPDATE_EXPECTED_DATE_CONFIG) {
    this.store.dispatch(new FactoryConfigActions.UpdateFactoryExpectedDateConfig(payload));
  }

  updateTnaTargetUpdatesConfig(payload: TREQUEST_TNA_TARGET_UPDATES_CONFIG_UPDATE) {
    this.store.dispatch(new FactoryConfigActions.UpdateTnaTargetUpdatesConfig(payload));
  }

  getTnaTargetUpdatesConfigLoader() {
    return this.store.pipe(select(FactoryListSelectors.getTnaTargetUpdatesConfigLoader));
  }

  fetchAutoPlanConfig() {
    this.store.dispatch(new FactoryConfigActions.GetAutoPlanConfig());
  }

  setAutoPlanConfigLoader(showLoader: boolean) {
    this.store.dispatch(new FactoryConfigActions.SetAutoPlanConfigLoader(showLoader));
  }

  getAutoPlanConfig() {
    return this.store.pipe(select(FactoryListSelectors.getAutoPlanConfig));
  }

  getAutoPlanConfigLoader() {
    return this.store.pipe(select(FactoryListSelectors.getAutoPlanConfigLoader));
  }

  updateAutoPlanConfig(payload: TREQUEST_UPDATE_AUTO_PLAN_CONFIG) {
    this.store.dispatch(new FactoryConfigActions.UpdateAutoPlanConfig(payload));
  }
}

export type TGetFactoryEfficiencyConfig = ReturnType<FactoryConfigFacade['getFactoryEfficiencyConfig']>;
export type TGetFactoryBenchmarkingConfig = ReturnType<FactoryConfigFacade['getFactoryBenchmarkingConfig']>;
export type TGetFactoryOtlOplConfig = ReturnType<FactoryConfigFacade['getFactoryOtlOplConfig']>;
export type TGetFactorySedConfig = ReturnType<FactoryConfigFacade['getFactorySedConfig']>;
export type TGetFactoryExpectedDateConfig = ReturnType<FactoryConfigFacade['getFactoryExpectedDateConfig']>;
